import { useCallback, useMemo, useState } from 'react';

import { Radio, Space, Notification } from '@bringg/react-components';
import { InfoTooltip } from '@bringg-frontend/ui';

import DialogBox from '../../components/dialog-box/dialog-box';
import { ActionType, type CalendarEvent, type EventFormData } from '../data-utils';
import { useStores } from 'bringg-web/recipes';
import { useSpeedFactorTranslation } from '../translations';
import { SpeedFactorGroup } from '../speed-factor-grouping/types';
import { formDataManager } from '../utils/form-data-manager';
import { eventDataManager } from '../utils/event-data-manager';

type Props = {
	open: boolean;
	onOk: () => void;
	onCancel: () => void;
	event: CalendarEvent;
	groupedSpeedFactors: SpeedFactorGroup[];
	values?: EventFormData;
	timezone?: string;
};

const UpdateDialog = ({ open, onOk, onCancel, event, values, groupedSpeedFactors, timezone }: Props) => {
	const { serviceAreasStore } = useStores();
	const [loading, setLoading] = useState(false);
	const [actionType, setActionType] = useState<ActionType>('all');

	const translations = useSpeedFactorTranslation();

	const options = useMemo(() => {
		return [
			{ label: translations.updateCurrent, value: 'current' },
			{
				label: translations.updateCurrentAndFollowing,
				value: 'current_and_following',
				tooltip: translations.updateAllTooltip
			},
			{
				label: translations.updateAll,
				value: 'all',
				tooltip: translations.updateAllTooltip
			}
		];
	}, [translations]);

	const handleActionChange = e => {
		setActionType(e.target.value);
	};

	const handleOk = useCallback(async () => {
		setLoading(true);

		try {
			const response = await serviceAreasStore.updateSpeedFactor(
				formDataManager.normalizeUpdateData({ formData: values, event, timezone }),
				eventDataManager.normalizeEventData(event, timezone),
				actionType,
				groupedSpeedFactors,
				timezone
			);

			Notification.success(response.message);

			onOk();
		} catch (error) {
			const message = error instanceof Error ? error.message : String(error);

			Notification.error(message);
		} finally {
			setLoading(false);
		}
	}, [event, values, actionType, onOk, serviceAreasStore, groupedSpeedFactors, timezone]);

	return (
		<DialogBox
			title={translations.updateTitle}
			subtitle={translations.updateSubtitle}
			open={open}
			onOk={() => handleOk()}
			onCancel={onCancel}
			okText={translations.updateOk}
			okButtonProps={{ disabled: loading }}
			destroyOnClose
		>
			<Radio.Group value={actionType} onChange={handleActionChange}>
				<Space direction="vertical" size="small">
					{options.map(option => (
						<Radio value={option.value} style={{ fontWeight: 400 }} key={option.value}>
							{option.label}
							{option.tooltip && <InfoTooltip title={option.tooltip} />}
						</Radio>
					))}
				</Space>
			</Radio.Group>
		</DialogBox>
	);
};

export default UpdateDialog;
